import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, serverTimestamp, getDoc, addDoc } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { useLocation } from "react-router-dom";
import FrontInventoryView from "../components/frontInventoryView";
import BackInventoryView from "../components/backInventoryView";
import GlassesSpinner from "../components/glassesSpinner";

const InventoryPage = ({ user }) => {
  const [items, setItems] = useState({ inStock: [], outOfStock: [] });
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [loading, setLoading] = useState(true);
  const inventoryRef = collection(db, "inventory");
  const location = useLocation();
  const view = new URLSearchParams(location.search).get("view");

  useEffect(() => {
    const fetchData = async () => {
      const start = Date.now();
      const snapshot = await getDocs(inventoryRef);
      const rawData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const grouped = {};

      rawData.forEach(item => {
        const brand = item.name || "Unknown";
        if (!grouped[brand]) {
          grouped[brand] = { brand, visible: 0, backdoor: 0 };
        }
        grouped[brand].visible += item.visible || 0;
        grouped[brand].backdoor += item.backdoor || 0;
      });

      const all = Object.values(grouped);
      const inStock = all.filter(item => item.visible > 0 || item.backdoor > 0);
      const outOfStock = all.filter(item => item.visible === 0 && item.backdoor === 0);

      setItems({ inStock, outOfStock });

      const elapsed = Date.now() - start;
      const remaining = 1000 - elapsed;
      if (remaining > 0) setTimeout(() => setLoading(false), remaining);
      else setLoading(false);
    };

    fetchData();
  }, []);

  const handleUpdate = async (itemId, field, value) => {
    const ref = doc(db, "inventory", itemId);
    const docSnap = await getDoc(ref);

    if (!docSnap.exists()) {
      console.warn("Item not found in Firestore:", itemId);
      return;
    }

    const item = docSnap.data();
    const oldValue = item[field] ?? 0;
    const newValue = parseInt(value);

    if (isNaN(newValue) || newValue < 0) {
      console.warn("Invalid new value:", value);
      return;
    }

    if (newValue === oldValue) {
      console.log("No change in value — skipping update.");
      return;
    }

    console.log(`🟡 BEFORE update — [${field}]:`, oldValue);
    console.log(`🟢 REQUESTED new value for [${field}]:`, newValue);

    await updateDoc(ref, {
      [field]: newValue,
      lastUpdated: serverTimestamp(),
    });

    console.log(`✅ AFTER update — sent value to Firestore:`, newValue);

    const userDoc = await getDoc(doc(db, "users", user.uid));
    const fullName = `${userDoc.data()?.firstName || ""} ${userDoc.data()?.lastName || ""}`.trim();

    await addDoc(collection(db, "logs"), {
      user: fullName || user.email,
      brand: item.name || "Unknown",
      item: item.name || "Unknown",
      showcase: item.section ?? "Front",
      field,
      old: oldValue,
      new: newValue,
      timestamp: serverTimestamp(),
    });
  };

  if (view === "back") return <BackInventoryView user={user} handleUpdate={handleUpdate} />;
  if (view === "front") return <FrontInventoryView user={user} handleUpdate={handleUpdate} />;
  if (loading) return <GlassesSpinner />;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Inventory</h1>
      <table className="w-full border">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-2 text-left">Brand</th>
            <th className="p-2 text-center">Visible</th>
            <th className="p-2 text-center">Backdoor</th>
          </tr>
        </thead>
        <tbody>
          {[...items.inStock, ...(showOutOfStock ? items.outOfStock : [])].map((item, index) => (
            <tr key={index} className="border-t even:bg-gray-50">
              <td className="p-2">{item.brand}</td>
              <td className="p-2 text-center">{item.visible}</td>
              <td className="p-2 text-center">{item.backdoor}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {items.outOfStock.length > 0 && !showOutOfStock && (
        <div className="mt-4 text-center">
          <button
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
            onClick={() => setShowOutOfStock(true)}
          >
            Show Out of Stock
          </button>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
